import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setCsrfToken } from "./api";

export interface Profile {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  email: string | null;
  emailVerified: boolean;
  hasPassword: boolean;
  locale: "ru" | "en";
  createdAt: string;
  roles: string[];
  mfaEnabled: boolean;
  identities: string[];
  passkeys: number;
}

export type SessionState =
  | { authenticated: false }
  | { authenticated: true; mfaPending: boolean; mfaVerified: boolean; csrfToken: string; sessionId: string; reauthValidUntil: string | null; user: Profile };

export interface PublicConfig {
  vkIdEnabled: boolean;
  yandexEnabled: boolean;
  githubEnabled: boolean;
  passkeysEnabled: boolean;
  passwordAuthEnabled: boolean;
  testMode: boolean;
  supportEmail: string;
  entitlementOfflineDays: number;
}

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const s = await api<SessionState>("GET", "/session");
      setCsrfToken(s.authenticated ? s.csrfToken : "");
      return s;
    },
    staleTime: 30_000,
  });
}

export function useConfig() {
  return useQuery({ queryKey: ["config"], queryFn: () => api<PublicConfig>("GET", "/config"), staleTime: 300_000 });
}

export function useRefreshSession() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["session"] });
}

export const isReauthFresh = (s: SessionState | undefined) =>
  !!s && s.authenticated && !!s.reauthValidUntil && new Date(s.reauthValidUntil).getTime() > Date.now() + 15_000;
